// eslint-disable-next-line jira/restricted/react
import React, { type ComponentType, PureComponent, type KeyboardEvent } from 'react';
import { styled, css } from '@compiled/react';
import keycode from 'keycode';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import type { IntlShapeV2 as IntlShape } from '@atlassian/jira-intl/src/v2/types.tsx';
import type { IssueId } from '@atlassian/jira-shared-types/src/general.tsx';
import { isBaseLevel } from '../../../../model/hierarchy';
import { ITEM_HEIGHT_BASE_LEVEL } from '../../../../model/table';
import { DEFAULT_LEVEL } from '../../common/constants';
import { getRowStyle } from '../../common/context/roadmap-style-marshal';
import RoadmapStyleMarshalContext, {
	type RoadmapStyleMarshalProps,
} from '../../common/context/roadmap-style-marshal/context/index.tsx';
import type { OnItemExpandChanged } from '../../common/types.tsx';
import type { APIProps as ListItemContentProps } from '../list-item-content';
import type { APIProps as ListItemContentTooltipProps } from '../list-item-content-tooltip';
import Chevron from './chevron/view.tsx';
import messages from './messages';

type APIProps = {
	isSelected: boolean;
	isSelectEnabled: boolean;
	isParent: boolean;
	isExpanded: boolean;
	level: number;
	id: IssueId;
	parentId: IssueId | undefined;
	itemHeight: number;
	Content: ComponentType<ListItemContentProps>;
	ContentTooltip: ComponentType<ListItemContentTooltipProps>;
	onClick: (id: IssueId, level: number) => void;
	onExpandChanged: OnItemExpandChanged;
};

type Props = APIProps & {
	intl: IntlShape;
};

// eslint-disable-next-line jira/react/no-class-components
class ListItem extends PureComponent<Props> {
	static defaultProps = {
		isParent: false,
		isExpanded: false,
		isSelected: false,
		isSelectEnabled: true,
		level: DEFAULT_LEVEL,
	};

	// go/jfe-eslint
	// eslint-disable-next-line react/sort-comp
	static contextType = RoadmapStyleMarshalContext;

	componentDidMount() {
		this.updateRowStyles();
	}

	componentDidUpdate(prevProps: Props) {
		if (prevProps.isSelected !== this.props.isSelected) {
			this.updateRowStyles();
		}
	}

	onSetContainerRef = (element: HTMLDivElement | null) => {
		this.containerRef = element;
	};

	onSetContentContainerRef = (element: HTMLDivElement | null) => {
		this.contentContainerRef = element;
	};

	// ================== //
	// ENTER/LEAVE EVENTS //
	// ================== //

	onRowMouseEnter = () => {
		const { id } = this.props;
		const { onRowMouseEnter } = this.context;

		onRowMouseEnter(id);
	};

	onRowMouseLeave = () => {
		const { id } = this.props;
		const { onRowMouseLeave } = this.context;

		onRowMouseLeave(id);
	};

	// ============ //
	// CLICK EVENTS //
	// ============ //

	onContentClick = () => {
		const { id, onClick, level } = this.props;
		onClick(id, level);
	};

	onContentKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
		if (event.keyCode === keycode('enter') || event.keyCode === keycode('space')) {
			event.preventDefault();
			this.onContentClick();
		}
	};

	onChevronClick = () => {
		const { id, isExpanded, onExpandChanged } = this.props;
		onExpandChanged(id, isExpanded);
	};

	onChevronKeyDown = () => {
		const { id, isExpanded, onExpandChanged } = this.props;
		onExpandChanged(id, isExpanded);
	};

	updateRowStyles() {
		const { id, isSelected, isSelectEnabled } = this.props;
		const { onRowSelect, onRowDeselect } = this.context;

		if (isSelectEnabled) {
			isSelected ? onRowSelect(id) : onRowDeselect(id);
		}
	}

	// @ts-expect-error - TS2564 - Property 'context' has no initializer and is not definitely assigned in the constructor.
	context: RoadmapStyleMarshalProps;

	containerRef: HTMLDivElement | null = null;

	contentContainerRef: HTMLDivElement | null = null;

	render() {
		const {
			id,
			parentId,
			itemHeight,
			level,
			isParent,
			isSelectEnabled,
			isExpanded,
			intl: { formatMessage },
			Content,
			ContentTooltip,
		} = this.props;

		const contentContainer = (
			<ContentContainer
				data-testid={`deployments.common.components.table.components.list-item.base.content-container-${id}`}
				ref={this.onSetContentContainerRef}
				tabIndex={0}
				isParent={isParent}
				level={level}
				onClick={this.onContentClick}
				onKeyDown={this.onContentKeyDown}
			>
				{isParent && (
					<Chevron
						onClick={this.onChevronClick}
						onKeyDown={this.onChevronKeyDown}
						isExpanded={isExpanded}
						label={formatMessage(messages.chevronLabel)}
					/>
				)}
				<Content id={id} />
			</ContentContainer>
		);

		return (
			<Container
				data-testid="deployments.common.components.table.components.list-item.container"
				ref={this.onSetContainerRef}
				isSelectEnabled={isSelectEnabled}
				itemHeight={isBaseLevel(level) ? ITEM_HEIGHT_BASE_LEVEL : itemHeight}
				onMouseEnter={this.onRowMouseEnter}
				onMouseLeave={this.onRowMouseLeave}
				onFocus={this.onRowMouseEnter}
				onBlur={this.onRowMouseLeave}
				{...getRowStyle(id, parentId)}
			>
				<ContentTooltip id={id}>{contentContainer}</ContentTooltip>
			</Container>
		);
	}
}

export interface BaseItemStylesProps {
	itemHeight: number;
}

// RLSS-2812 replace the first color.background.input to color.background.accent.gray.subtlest
//           replace the second color.background.neutral to color.background.accent.gray.subtler
//           Consider finding even a better token than the suggested ones here.
const baseItemStyles = css<BaseItemStylesProps>({
	position: 'relative',
	display: 'flex',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	height: `${({ itemHeight }: BaseItemStylesProps) => itemHeight}px`,
	transition: 'background-color 100ms linear',
	'-RowBackgroundColor':
		'var( --item-selected-color, var(--item-hover-color, var(--base-background-color)) )',
	backgroundColor: 'var(--row-background-color)',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	'-BaseBackgroundColor': token('color.background.input', colors.N0),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&:nth-of-type(odd)': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		'-BaseBackgroundColor': token('color.background.input.hovered', colors.N10),
	},
});

const BASE_LEVEL_CONTENT_PADDING = gridSize * 6;
const DEFAULT_CONTENT_PADDING = gridSize * 3;

const getContentPadding = (isParent: boolean, level: number) => {
	if (isParent) {
		return 0;
	}
	if (isBaseLevel(level)) {
		return BASE_LEVEL_CONTENT_PADDING;
	}

	return DEFAULT_CONTENT_PADDING;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getMouseCursor = (props: any) => {
	if (props.isSelectEnabled) {
		return 'pointer';
	}
	return 'default';
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any, @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
const Container = styled.div<any>(baseItemStyles, {
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	cursor: getMouseCursor,
});

const boxShadow = `0 0 0 2px ${token('elevation.shadow.raised', colors.B100)}`;

/* A focus border that appears only when focus is attained via the keyboard.
 * Implemented via the .focus-visible class that comes from the 'focus-visible' polyfill.
 * Native outline is always disabled as as we either have:
 * 1) A mouse user who does not need the focus style
 * 2) A keyboard user, who will see our custom focus border
 */

const focusBorder = css({
	outline: 'none',
	transition: 'box-shadow 0.2s cubic-bezier(0.47, 0.03, 0.49, 1.38)',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'&:focus.focus-visible': {
		boxShadow,
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentContainer = styled.div<{ isParent: boolean; level: number }>(
	{
		display: 'flex',
		alignItems: 'center',
		flexGrow: 1,
		overflow: 'hidden',
		height: '100%',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		paddingLeft: ({ isParent, level }) => `${getContentPadding(isParent, level)}px`,
		paddingRight: token('space.100', '8px'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	focusBorder,
);

export default injectIntl(ListItem);

export type { APIProps };
